<template>
  <div>
    <v-layout row align-center align-middle class="fullWidth" id="categoryContainer">
      <v-flex xs12>
        <!-- Mobile Dropdown (only visible on phones) -->
        <div v-if="isMobile" class="dropdown-container">
          <v-menu offset-y class="dropdown-menu">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="dropdown-activator"
                v-bind="attrs"
                v-on="on"
                text
              >
                <strong>{{ getSelectedCategoryName() }}</strong>
                <v-icon right>unfold_more</v-icon>
              </v-btn>
            </template>
            <v-list class="dropdown-list">
              <v-list-item
                v-for="category in menus"
                :key="category.code"
                @click="_scrollIntoView(category)"
                class="dropdown-item"
                :class="{ 'selected-item': activeTab === category.anchor }"
              >
                <v-list-item-title>
                  <strong>{{ category.name }}</strong>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <!-- Desktop Button Bar (tablets, laptops, and desktops) -->
        <div v-if="!isMobile" class="menu-buttons-container">
          <v-btn
            v-for="category in menus"
            :key="category.code"
            class="menu-category-btn"
            :class="{ 'activeClass': activeTab == category.anchor }"
            @click="_scrollIntoView(category)"
            text
          >
            <strong>{{ category.name }}</strong>
          </v-btn>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  props: {
    menus: Array
  },

  data() {
    return {
      activeTab: null,
      lastCheck: 0,
      manualTabSelect: false,
      scrollDebounceTimer: null
    }
  },

  computed: {
    ...mapGetters(['getOrderType', 'isMobile'])
  },

  methods: {
    getSelectedCategoryName() {
      return this.$vuetify.t('$vuetify.menu.dropdown') || 'Menu Dropdown';
    },

    async _scrollIntoView(category) {
      this.manualTabSelect = true;

      for (let index in this.menus) {
        if (this.$parent.filteredCategories[index] && !this.$parent.filteredCategories[index].render) {
          this.$set(this.$parent.filteredCategories[index], 'render', true)
          this.$parent.$forceUpdate()
          await this.$nextTick()
        }
        if (this.$parent.filteredCategories[index].anchor == category.anchor)
          break
      }

      this.activeTab = category.anchor;
      await this.$nextTick()
      await new Promise(resolve => requestAnimationFrame(resolve))
      const el = document.getElementById(category.anchor)
      if (el) {
        const elDistanceToTop = window.pageYOffset + el.getBoundingClientRect().top - 64 - 60 - 20;
        window.scroll({
          top: elDistanceToTop,
          behavior: 'smooth'
        });
      }
    },

    matchTab() {
      const currentHash = this.$route.hash;
      const matchRoute = () => this.menus.find(cat => '#' + cat.anchor === currentHash);
      const matched = matchRoute();
      this.activeTab = (matched || this.menus[0] || {}).anchor;
      if (currentHash && matched) {
        this._scrollIntoView((matched))
      }
    },

    handlePageScroll() {
      if (this.manualTabSelect) {
        clearTimeout(this.scrollDebounceTimer);
        this.scrollDebounceTimer = setTimeout(() => {
          this.manualTabSelect = false;
        }, 150);
        return;
      }

      this.checkForLazyLoading();
      
      const menuContainers = Object.values(document.getElementsByClassName('menuContainer'))
      if (!menuContainers.length) return;
      
      for (let container of menuContainers) {
        const rect = container.getBoundingClientRect();
        if (rect.top < window.innerHeight/2 && rect.bottom > 0) {
          this.activeTab = container.id;
          break;
        }
      }
    },
    
    checkForLazyLoading() {
      const now = Date.now();
      if (now - this.lastCheck < 200) return;
      this.lastCheck = now;
      
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const buffer = viewportHeight * 1.5;
      
      if (this.$parent.filteredCategories) {
        for (let i = 0; i < this.$parent.filteredCategories.length; i++) {
          const category = this.$parent.filteredCategories[i];
          if (category.render) continue;
          
          const el = document.getElementById(category.anchor);
          if (!el) continue;
          
          const rect = el.getBoundingClientRect();
          const elementTop = scrollY + rect.top;
          
          if (elementTop < scrollY + viewportHeight + buffer) {
            this.$set(this.$parent.filteredCategories[i], 'render', true);
            this.$parent.$forceUpdate();
          }
        }
      }
    },
    
    hideSearch() {
      if (this.$parent.searchText) {
        this.$parent.searchText = null;
      }
    }
  },

  watch: {
    getOrderType: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.matchTab()
          }, 75)
        })
      }
    }
  },

  mounted() {
    if (this.$parent.filteredCategories && this.$parent.filteredCategories.length > 0) {
      this.$set(this.$parent.filteredCategories[0], 'render', true);
      this.$parent.$forceUpdate();
    }
    
    setTimeout(() => {
      try {
        this.handlePageScroll();
        
        this.checkForLazyLoading();
      } finally {
        window.addEventListener('scroll', this.handlePageScroll, { passive: true });
      }
    }, 1000)
  },

  destroyed() {
    window.removeEventListener('scroll', this.handlePageScroll);
  }
}
</script>

<style>
  .categoryTabs {
    height: auto;
    background-color: white;
    border-bottom: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* Mobile Dropdown Styles (Only visible on mobile) */
  .dropdown-container {
    display: flex;
    justify-content: center;
    padding: 8px 16px;
  }

  .dropdown-activator {
    background-color: white !important;
    color: var(--v-primary-base) !important;
    border: 2px solid var(--v-primary-base) !important;
    border-radius: 8px !important;
    text-transform: none !important;
    font-weight: 500 !important;
    letter-spacing: 0 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.3s ease !important;
    font-size: 18px !important;
    height: auto !important;
    min-height: 44px !important;
    font-family: 'Bebas Neue', sans-serif !important;
    min-width: calc(100vw - 64px) !important;
    max-width: calc(100vw - 64px) !important;
    padding: 12px 16px !important;
    justify-content: space-between !important;
  }

  .dropdown-activator .v-btn__content {
    justify-content: space-between !important;
    width: 100% !important;
  }

  .dropdown-activator .v-icon {
    margin-left: auto !important;
    font-size: 20px !important;
  }

  .dropdown-activator:hover {
    background-color: #f8f9fa !important;
    border-color: #B12E21 !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  }

  .dropdown-activator:hover .v-icon {
    color: #B12E21 !important;
  }

  .dropdown-list {
    border-radius: 10px !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    max-height: 400px !important;
    overflow-y: auto !important;
    min-width: calc(100vw - 64px) !important;
    max-width: calc(100vw - 64px) !important;
  }

  .dropdown-item {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    padding-top: 10px;  
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 10px;
    font-size: 18px;
  }

  .dropdown-item:hover {
    background-color: #f8f8f8 !important;
  }

  .dropdown-item.selected-item {
    background-color: #C73628 !important;
    color: white !important;
  }

  .dropdown-item.selected-item:hover {
    background-color: #B12E21 !important;
  }

  .dropdown-item:last-child {
    border-bottom: none !important;
  }

  .v-list-item__content {
    padding: 0 !important;
  }

  .v-list-item__title {
    font-weight: 500 !important;
    line-height: 1.5 !important;
  }

  /* Desktop Button Styles (preserved from original) */
  .menu-buttons-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    padding: 12px 0;
  }

  .menu-category-btn {
    background-color: white !important;
    color: var(--v-primary-base) !important;
    border-radius: 10px !important;
    text-transform: none !important;
    font-weight: 500 !important;
    margin: 4px !important;
    letter-spacing: 0 !important;
    box-shadow: none !important;
    transition: all 0.3s ease !important;
    font-size: 20px !important;
    height: auto !important; 
    min-height: 36px !important; 
    font-family: 'Bebas Neue', sans-serif !important;
    
    min-width: 0 !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }

  .v-btn.menu-category-btn .v-btn__content {
    padding: 0 !important;
    height: auto !important;
    line-height: 1.5 !important;
    white-space: nowrap !important;
  }

  .menu-category-btn.activeClass {
    background-color: var(--v-primary-base) !important;
    color: white !important;
  }
  
  @media only screen and (max-width: 600px) {
    .menu-buttons-container {
      padding: 8px 0;
    }
    
    .menu-category-btn {
      max-width: 90% !important;
    }
    
    .v-btn.menu-category-btn .v-btn__content {
      text-overflow: ellipsis !important;
      overflow: hidden !important;
      max-width: 85vw !important;
      min-width: 90px !important;
      display: block !important;
    }
  }
</style>